import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './components/AdminNavigation';
import Footer from './components/Footer';
import useProgressPolling from './utils/useProgressPolling';
import { getApiUrl } from './config';
import { getAccessToken, logout } from './utils/auth';
import OfflineHint, { useOnlineOnly } from './components/OfflineHint';

// file_status values (unified v0.12.0): not_started | running | done | failed.
// Phase 1 (extraction) is pending while file_status is not_started or running.
const PHASE1_PENDING = new Set(['not_started', 'running']);

function statusLabel(status) {
  switch (status) {
    case 'not_started': return 'Queued';
    case 'running': return 'Extracting…';
    case 'done': return 'Ready';
    case 'failed': return 'Failed';
    default: return status || 'Unknown';
  }
}

function statusColor(status) {
  if (status === 'done') return 'text-green-600 dark:text-green-400';
  if (status === 'running') return 'text-amber-600 dark:text-amber-400';
  if (status === 'failed') return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

function FileRow({ file, index, progressFiles }) {
  const progressFile = progressFiles?.find((pf) => pf.file_id === file.file_id);
  const liveStatus = progressFile?.file_status ?? file.file_status;

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <span className="text-sm text-gray-500 dark:text-gray-400 w-5 shrink-0 text-right">
        {file.position ?? index + 1}.
      </span>
      <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">
        {file.title || 'Untitled file'}
      </span>
      <span className={`inline-flex items-center gap-1 text-xs font-medium shrink-0 ${statusColor(liveStatus)}`}>
        {liveStatus === 'running' && (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {statusLabel(liveStatus)}
      </span>
    </div>
  );
}

function ProjectUpload() {
  const offline = useOnlineOnly();
  const { projectId } = useParams();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [linkUrl, setLinkUrl] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [linkError, setLinkError] = useState('');

  const fileInputRef = useRef(null);

  const isPollingEnabled = files.some((f) => PHASE1_PENDING.has(f.file_status));

  const { snapshot, isPolling } = useProgressPolling(projectId, isPollingEnabled, {
    isDone: (snap) => !snap?.files?.some((f) => PHASE1_PENDING.has(f.file_status)),
    intervalMs: 3000,
  });

  useEffect(() => {
    if (!snapshot?.files) return;
    setFiles((prev) =>
      prev.map((f) => {
        const live = snapshot.files.find((sf) => sf.file_id === f.file_id);
        return live ? { ...f, file_status: live.file_status } : f;
      }),
    );
  }, [snapshot]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAccessToken();
      if (!token) { logout(); return; }

      const projectRes = await axios.get(getApiUrl(`projects/${projectId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      const proj = projectRes.data;
      setProject(proj);
      setFiles(proj.files ?? []);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      if (err?.response?.status === 404) { setError('Project not found.'); } else if (err?.request) { setError('Network error: Could not connect to server.'); } else { setError('An unexpected error occurred.'); }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length > 0) handleUpload(picked);
    e.target.value = '';
  };

  const handleUpload = useCallback(async (picked) => {
    const token = getAccessToken();
    if (!token) { logout(); return; }

    setUploading(true);
    setUploadError('');

    for (const file of picked) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(
          getApiUrl(`projects/${projectId}/upload`),
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const uploaded = res.data;
        setFiles((prev) => [
          ...prev,
          {
            file_id: uploaded.file_id,
            position: uploaded.position,
            title: file.name,
            file_status: 'not_started',
            audio_status: 'not_started',
            blitz_status: 'not_started',
            source_type: 'uploaded',
          },
        ]);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.response?.data?.detail;
        setUploadError(msg || `Failed to upload ${file.name}.`);
        break;
      }
    }

    setUploading(false);
  }, [projectId]);

  /**
   * Add a web page as a source.
   *
   * The server does not fetch the page here — it writes the rows and returns,
   * leaving a headless render to the file watcher (spec 024 FR-002). So the
   * row arrives with no title, and the address stands in for one until a
   * later load has the real thing.
   */
  const handleAddLink = useCallback(async (e) => {
    e.preventDefault();
    const url = linkUrl.trim();
    if (!url) return;

    const token = getAccessToken();
    if (!token) { logout(); return; }

    setAddingLink(true);
    setLinkError('');

    try {
      const res = await axios.post(
        getApiUrl(`projects/${projectId}/links`),
        { url },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const added = res.data;
      setFiles((prev) => [
        ...prev,
        {
          file_id: added.file_id,
          position: added.position,
          title: added.source_url || url,
          file_status: 'not_started',
          audio_status: 'not_started',
          blitz_status: 'not_started',
          source_type: added.source_type || 'link',
        },
      ]);
      setLinkUrl('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) { logout(); return; }
      const detail = err?.response?.data?.message || err?.response?.data?.detail;
      // "Too many today" and "that address was refused" are deliberately
      // distinguishable and want different responses from the owner (FR-003b).
      if (status === 429) {
        setLinkError(detail || 'You have added too many links today. Try again tomorrow.');
      } else if (status === 422) {
        setLinkError(detail || 'That address was refused. Check it and try again.');
      } else if (status === 404) {
        setLinkError('This server does not support adding links yet.');
      } else if (err?.request) {
        setLinkError('Network error: Could not connect to server.');
      } else {
        setLinkError(detail || 'Failed to add that link.');
      }
    } finally {
      setAddingLink(false);
    }
  }, [projectId, linkUrl]);

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
        <AdminNavigation />
        <div className="flex flex-grow items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
        <AdminNavigation />
        <div className="flex-grow container mx-auto px-4 py-8">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <Link to="/dashboard" className="mt-2 inline-block text-sm text-red-600 underline">
              Back to Dashboard
            </Link>
          </div>
        </div>
        <Footer desktopOnly />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
      <AdminNavigation />

      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-2xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary mb-2 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {project?.title || 'Upload Files'}
          </h1>
        </div>

        {/* Extraction progress banner */}
        {isPollingEnabled && isPolling && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
            <svg className="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Extracting chapters from your files… This may take a moment.
          </div>
        )}

        {/* File list */}
        <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm overflow-hidden mb-6">
          {files.length === 0 ? (
            <p className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
              Nothing here yet. Upload a PDF or EPUB below, or add a web page.
            </p>
          ) : (
            files.map((file, idx) => (
              <FileRow
                key={file.file_id}
                file={file}
                index={idx}
                progressFiles={snapshot?.files}
              />
            ))
          )}
        </div>

        {/* Upload area */}
        <div className="mb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.epub"
            multiple
            className="hidden"
            onChange={handleFilePick}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || offline}
            className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                <span className="text-sm">Uploading…</span>
              </>
            ) : (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="text-sm font-medium">Click to upload PDF or EPUB</span>
                <span className="text-xs">Multiple files allowed</span>
              </>
            )}
          </button>
          {uploadError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{uploadError}</p>
          )}

          {/* Or point at a page instead of uploading one */}
          <div className="mt-4 flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
            <span className="text-xs text-gray-500 dark:text-gray-400">or</span>
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
          </div>

          <form onSubmit={handleAddLink} className="mt-4">
            <label
              htmlFor="link-url"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Add a web page
            </label>
            <div className="flex gap-2">
              {/* type="url" so the browser rejects a bare hostname itself */}
              <input
                id="link-url"
                type="url"
                required
                maxLength={2048}
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                disabled={addingLink || offline}
                placeholder="https://example.com/an-article"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={addingLink || offline || !linkUrl.trim()}
                className="flex h-10 shrink-0 items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingLink ? 'Adding…' : 'Add'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              The page is fetched in the background and added as a chapter.
            </p>
            {linkError && (
              <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
                {linkError}
              </p>
            )}
          </form>

          <OfflineHint action="Uploading" />
        </div>

        {/* Continue to the Chapters page, where audio generation is triggered */}
        {files.length > 0 && (
          <div className="mt-4 text-center">
            <Link
              to={`/projects/${projectId}/chapters`}
              className="inline-block w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Continue to chapters →
            </Link>
          </div>
        )}
      </div>

      <Footer desktopOnly />
    </div>
  );
}

export default ProjectUpload;
